// /src/components/imovel/BannerCard.jsx
// VERSÃO 1.1 SUPREMA - TIM UNITY BANNER CARD (DUAL ACTION: VIEW & LINK)

import React from 'react';
import { 
    FiLink, FiZap, FiEye, FiTrash2, FiExternalLink, FiLayout, FiCheckCircle, FiMaximize2 
} from 'react-icons/fi'; 
import { motion } from 'framer-motion';

// 📡 RESOLVEDOR DE IMAGENS TÁTICO
const resolveImagePath = (path) => {
    if (!path) return 'https://via.placeholder.com/1200x400?text=AGUARDANDO+MIDIA+TIM';
    if (path.startsWith('http')) return path;
    
    const formattedPath = path.startsWith('/') ? path : `/${path}`;
    const baseUrl = import.meta.env.VITE_API_BASE_URL 
        ? import.meta.env.VITE_API_BASE_URL.replace('/api', '') 
        : ''; 
        
    return `${baseUrl}${formattedPath}`;
};

export default function BannerCard({ banner, onAction, isAdmin = false }) {
    
    if (!banner) return null; 

    const imageUrl = resolveImagePath(banner.imagemUrl);

    return (
        <motion.div 
            whileHover={{ y: -8 }}
            className="group relative w-full h-[320px] md:h-[380px] rounded-[3rem] overflow-hidden border shadow-2xl transition-all duration-700"
            style={{ 
                backgroundColor: 'var(--bg-card)', 
                borderColor: 'var(--border-color)'
            }}
        >
            {/* 🖼️ FONDO CINEMATOGRÁFICO */}
            <div className="absolute inset-0 bg-black">
                <img 
                    src={imageUrl} 
                    alt={banner.titulo} 
                    className="w-full h-full object-cover transition-transform duration-[5s] group-hover:scale-105 opacity-60 group-hover:opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent" />
            </div>

            {/* 🛠️ HUD DE FERRAMENTAS (TOP RIGHT) */}
            <div className="absolute top-6 right-6 z-30 flex gap-3 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-x-4 group-hover:translate-x-0">
                {/* Botão de Abrir/Inspecionar (Sempre visível no hover) */}
                <button 
                    onClick={() => onAction(banner._id, 'view', banner)}
                    className="p-4 rounded-2xl bg-white/10 text-white backdrop-blur-xl border border-white/10 hover:bg-accent hover:text-black transition-all shadow-2xl"
                    title="Inspecionar Banner"
                >
                    <FiMaximize2 size={20} />
                </button>

                {/* Botão de Excluir (Admin Only) */}
                {isAdmin && (
                    <button 
                        onClick={() => onAction(banner._id, 'delete')}
                        className="p-4 rounded-2xl bg-red-600/20 text-red-500 backdrop-blur-xl border border-red-500/20 hover:bg-red-600 hover:text-white transition-all shadow-2xl"
                        title="Incinerar Mídia"
                    >
                        <FiTrash2 size={20} />
                    </button>
                )}
            </div>

            {/* 📝 ZONA DE INTELIGÊNCIA (CONTEÚDO) */}
            <div className="absolute inset-0 p-10 flex flex-col justify-end items-start space-y-5">
                
                {/* Status & Position Badges */}
                <div className="flex gap-3">
                    <span className="backdrop-blur-xl bg-black/40 border border-white/10 px-4 py-1.5 rounded-full text-[8px] font-black text-white uppercase tracking-[0.2em] shadow-2xl">
                        <FiLayout className="inline mr-2 text-accent" /> {banner.posicao?.replace('_', ' ')}
                    </span>
                    {banner.status === 'Ativo' && (
                        <span className="backdrop-blur-xl bg-green-500/10 border border-green-500/20 px-4 py-1.5 rounded-full text-[8px] font-black text-green-500 uppercase tracking-[0.2em]">
                           <FiCheckCircle className="inline mr-2" /> Operacional
                        </span>
                    )}
                </div>

                <div className="max-w-2xl space-y-2">
                    <h3 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white leading-[0.85] drop-shadow-2xl transition-all group-hover:text-accent">
                        {banner.titulo}
                    </h3>
                    <p className="text-[10px] font-bold text-white/30 uppercase tracking-[0.3em] truncate max-w-md italic">
                        {banner.link ? `Uplink: ${banner.link}` : 'Mídia de Exposição Direta'}
                    </p>
                </div>

                {/* 🚀 BOTÕES DE AÇÃO DUPLA */}
                <div className="flex flex-wrap gap-4 pt-4">
                    {/* Ação 1: Inspecionar */}
                    <button 
                        onClick={() => onAction(banner._id, 'view', banner)}
                        className="px-8 py-4 rounded-2xl bg-white/5 border border-white/10 text-white font-black uppercase text-[10px] tracking-[0.3em] flex items-center gap-3 transition-all hover:bg-white/10 hover:border-white/40 active:scale-95"
                    >
                        <FiEye size={16} /> Inspecionar
                    </button>

                    {/* Ação 2: Destino (Se existir) */}
                    {banner.link && (
                        <a 
                            href={banner.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="group/btn relative px-8 py-4 rounded-2xl bg-accent text-black font-black uppercase text-[10px] tracking-[0.3em] flex items-center gap-4 transition-all hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(var(--accent-rgb),0.3)] overflow-hidden"
                        >
                            Acessar Destino <FiExternalLink size={16} className="group-hover/btn:translate-x-1 transition-transform" />
                            <div className="absolute inset-0 bg-white/20 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 skew-x-12" />
                        </a>
                    )}
                </div>
            </div>

            {/* 🔥 INDICADOR DE ATIVAÇÃO NEON */}
            <div className="absolute bottom-0 left-0 h-[6px] w-full scale-x-0 group-hover:scale-x-100 transition-transform duration-1000 origin-left"
                 style={{ 
                    backgroundColor: 'var(--accent-color)',
                    boxShadow: `0 0 30px var(--accent-color)` 
                 }} />
        </motion.div>
    );
}